import type { ApprovalLineDetail } from '../types/approval';

/**
 * 결재선의 **자리 이름** — 결재자인가 최종결재자인가 (2026-09-04 판 P1 · 2026-09-24 이름 바뀜).
 *
 * ## ★★★ 왜 화면이 셈하나
 *
 * 사용자 결정으로 결재선의 어휘가 **기안 → 결재자 → 최종결재자 · 참고자**가 됐다(2026-09-24). 그런데 저장은
 * 그대로다 — 둘 다 `lineType='APPROVE'` 이고 **마지막 줄이 「최종결재자」**, 그 앞이
 * 「결재자」다. 저장을 안 바꾼 덕에 마이그레이션도 판정 코드 변경도 없지만, 그 대가로
 * **읽는 쪽이 자리를 셈해야 한다.**
 *
 * ★★ 그래서 이 셈을 **한 곳에** 둔다. 결재란·Stepper·목록이 각자 세면 같은 줄이 화면마다
 *    다른 이름으로 뜨는 날이 온다(연차 잔여가 다섯 화면에서 갈렸던 것과 같은 부류).
 * ★★ 서버의 `slotOf`(`approval/default-line.ts`)와 **같은 규칙**이다 — 미리보기는 서버가
 *    `slot` 을 직접 주고, 이미 만들어진 문서는 여기서 센다.
 * ## ⛔ 2026-09-27 — 합의(AGREE)를 «다 지웠다»
 *
 * 사용자 지시 「전자결재에서 합의와 관련된 기능은 다 삭제해라」. 서버가 그 길을 닫았고(#1774)
 * 웹도 같은 날 걷었다 — 단추 · 깃발 · 배지 · 막대 · 상태 글자 · 계약 칸.
 * ```
 * 근거  ① 운영 AGREE 0        2026-09-26 8차 배포에서 배포서버가 실측
 *       ② 새 문서는 «구조상» 안 만든다   서버 planApprovalLines 가 결재자로 합친다(#1774)
 * ```
 * ⚠ 그래서 이 파일에 있던 「합의가 아직 남은 열 자리」 목록도 함께 없앴다 — 그 자리가 다 없어졌다.
 *   되살아나는 것은 `approval-role-words.guard.test.ts` 가 «0 으로» 막는다.
 * ⚠ `lineType` 이 `AGREE` 인 줄을 만나면(백업 복원 · 타 환경) 아래 셈이 **결재 줄로** 읽는다 —
 *   그 값만 따로 다루지 않는다. 참고자만 흐름에서 빠진다.
 */

export type LineSlotLabel = '결재자' | '최종결재자' | '참고자';

/** 진행을 막는 줄인가 — 참고자만 아니다. */
const BLOCKS = (lineType: string | undefined): boolean => (lineType ?? 'APPROVE') !== 'REFER';

/**
 * 그 줄의 자리 이름.
 *
 * @param lines 문서의 결재선 전부(순서대로)
 * @param index 이 줄이 몇 번째인가
 *
 * ★ **마지막 `APPROVE` 줄 하나만 「최종결재자」**다. 그 앞의 `APPROVE` 는 전부 「결재자」.
 * ★ 줄이 하나뿐이면 그 사람이 「최종결재자」다 — 그 앞이 없어도 된다.
 */
export function lineSlotLabel(
  lines: ReadonlyArray<Pick<ApprovalLineDetail, 'lineType'>>,
  index: number,
): LineSlotLabel {
  const kind = lines[index]?.lineType ?? 'APPROVE';
  if (kind === 'REFER') return '참고자';
  /*
    ⛔ 옛 `AGREE` 갈래를 지웠다 (2026-09-27). 그 값을 만나도 **아래 결재 셈이 그대로 읽는다** —
      마지막 `APPROVE` 줄이 「최종결재자」이고 그 앞이 「결재자」다. `AGREE` 는 그 셈에서
      `lastApproveIndex` 가 아니므로 **「결재자」로 뜬다** — 종전과 같은 결과다.
  */
  const lastApproveIndex = lines.reduce(
    (acc, l, i) => ((l.lineType ?? 'APPROVE') === 'APPROVE' ? i : acc),
    -1,
  );
  return index === lastApproveIndex ? '최종결재자' : '결재자';
}

/**
 * 참고자 줄만 — **하위에 명단으로** 그린다 (사용자 결정: 「하위에 참조자 명단만 보이면 된다」).
 *
 * ★ 참고자는 상태도 도장도 없다. 결재선에 섞어 그리면 「저 사람이 안 눌러서 멈춰 있다」로 읽힌다.
 */
export function referrerLines<T extends Pick<ApprovalLineDetail, 'lineType'>>(
  lines: ReadonlyArray<T>,
): T[] {
  return lines.filter((l) => !BLOCKS(l.lineType));
}

/** 진행을 막는 줄만 — 결재란·Stepper 가 그린다. */
export function approvalLines<T extends Pick<ApprovalLineDetail, 'lineType'>>(
  lines: ReadonlyArray<T>,
): T[] {
  return lines.filter((l) => BLOCKS(l.lineType));
}
